/**
 * 打卡热力图（GitHub 风格，一年视图）。
 *
 * 实现要点：
 *  - 53 列（周）× 7 行（周一到周日），一年刚好铺满；
 *  - 不引图表库，用 CSS grid + 固定小方块，手机上可以横向滚动；
 *  - **颜色只表达"做了几次"，不做价值判断** —— 儿童模式根本不显示这张图
 *    （见方案 2.5：隐藏统计图表，避免把孩子的注意力引到"比数字"上）。
 *  - 每天一个 title 属性（原生 tooltip），不做自绘浮层：家长主要在手机上用，
 *    点按比 hover 更可靠，而原生 title 在移动端虽不出提示，但方块本身可点，
 *    点击后会在下方显示那一天的详情。
 */
import { useMemo, useState } from 'react';
import { addDays, todayLocal } from '@fht/shared';
import type { HeatmapDay } from '../../lib/api/types';
import { cn } from '../../lib/utils';

const LEVEL_CLASSES = [
  'bg-slate-100', // 0
  'bg-emerald-200', // 1
  'bg-emerald-300', // 2
  'bg-emerald-400', // 3
  'bg-emerald-600', // 4
] as const;

const WEEKDAY_LABELS = ['一', '二', '三', '四', '五', '六', '日'];

/** 把 'YYYY-MM-DD' 转成 0=周一 … 6=周日（GitHub 的行顺序）。 */
function mondayIndex(date: string): number {
  const day = new Date(`${date}T00:00:00Z`).getUTCDay(); // 0=周日
  return (day + 6) % 7;
}

export interface HeatmapProps {
  year: number;
  days: HeatmapDay[];
  /** 高亮哪一天（默认家庭时区的今天）。 */
  highlightDate?: string;
}

export function Heatmap({ year, days, highlightDate }: HeatmapProps) {
  const [selected, setSelected] = useState<HeatmapDay | null>(null);

  const { grid, monthLabels, total, maxCount } = useMemo(() => {
    const byDate = new Map<string, HeatmapDay>();
    let max = 0;
    let sum = 0;
    for (const day of days) {
      byDate.set(day.date, day);
      max = Math.max(max, day.count);
      sum += day.count;
    }

    // 起点：该年 1 月 1 日所在周的周一
    const firstOfYear = `${year}-01-01`;
    const start = addDays(firstOfYear, -mondayIndex(firstOfYear));

    const weeks: Array<Array<HeatmapDay | null>> = [];
    const labels: Array<{ column: number; text: string }> = [];
    let cursor = start;
    let lastMonth = '';

    for (let week = 0; week < 54; week++) {
      const column: Array<HeatmapDay | null> = [];
      for (let row = 0; row < 7; row++) {
        const inYear = cursor.startsWith(String(year));
        column.push(
          inYear
            ? (byDate.get(cursor) ?? { date: cursor, count: 0, level: 0 })
            : null,
        );
        // 月份标签取该周第一天所属月份（换月时打点）
        if (row === 0 && inYear) {
          const month = cursor.slice(5, 7);
          if (month !== lastMonth) {
            lastMonth = month;
            labels.push({ column: week, text: `${Number(month)}月` });
          }
        }
        cursor = addDays(cursor, 1);
      }
      weeks.push(column);
      if (!cursor.startsWith(String(year)) && Number(cursor.slice(0, 4)) > year) break;
    }

    return { grid: weeks, monthLabels: labels, total: sum, maxCount: max };
  }, [days, year]);

  const today = highlightDate ?? todayLocal();

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="text-sm text-slate-500">
          共打卡 <span className="font-semibold text-slate-700">{total}</span> 次
          {maxCount > 0 && <span className="ml-1 text-xs">（单日最多 {maxCount} 次）</span>}
        </p>
        <div className="flex items-center gap-1 text-xs text-slate-400">
          <span>少</span>
          {LEVEL_CLASSES.map((cls) => (
            <span key={cls} className={cn('h-3 w-3 rounded-sm', cls)} aria-hidden="true" />
          ))}
          <span>多</span>
        </div>
      </div>

      {/* 手机上横向滚动；iPad / PC 上一年视图完整可见 */}
      <div className="-mx-1 overflow-x-auto px-1 pb-1">
        <div className="inline-block min-w-full">
          {/* 月份标签行 */}
          <div className="relative ml-6 h-4" aria-hidden="true">
            {monthLabels.map((label) => (
              <span
                key={`${label.column}-${label.text}`}
                className="absolute text-[10px] text-slate-400"
                style={{ left: `${label.column * 14}px` }}
              >
                {label.text}
              </span>
            ))}
          </div>

          <div className="flex gap-1">
            {/* 星期标签列 */}
            <div className="flex w-5 flex-col gap-[3px]" aria-hidden="true">
              {WEEKDAY_LABELS.map((label, index) => (
                <span
                  key={label}
                  className="flex h-3 items-center text-[10px] leading-none text-slate-400"
                  style={{ visibility: index % 2 === 0 ? 'visible' : 'hidden' }}
                >
                  {label}
                </span>
              ))}
            </div>

            {/* 格子 */}
            <div className="flex gap-[3px]">
              {grid.map((column, columnIndex) => (
                <div key={columnIndex} className="flex flex-col gap-[3px]">
                  {column.map((day, rowIndex) => {
                    if (!day) {
                      return <span key={rowIndex} className="h-3 w-3" aria-hidden="true" />;
                    }
                    // ⚠️ 这里的 minutes 是**服务端聚合后四舍五入到整分钟**的数
                    // （`stats.service.ts` 的 `Math.round(sum(actual_seconds) / 60)`），
                    // 秒在后端就没了 —— 所以不能拿 @fht/shared 的 formatDuration
                    // 去格式化它（那会把 12 显示成"12 分 0 秒"，凭空造出精度）。
                    // 要显示到秒得改后端聚合，不在"只做显示格式"的范围内。
                    const level = day.level ?? 0;
                    const isToday = day.date === today;
                    return (
                      <button
                        key={day.date}
                        type="button"
                        title={`${day.date}：${day.count} 次${day.minutes ? ` · ${day.minutes} 分钟` : ''}`}
                        aria-label={`${day.date} 打卡 ${day.count} 次`}
                        onClick={() => setSelected(day)}
                        className={cn(
                          'h-3 w-3 rounded-sm transition-transform hover:scale-125',
                          LEVEL_CLASSES[Math.min(4, Math.max(0, level))],
                          isToday && 'ring-2 ring-brand-500 ring-offset-1',
                        )}
                      />
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {selected && (
        <p className="rounded-xl bg-slate-50 px-3 py-2 text-sm text-slate-600">
          {selected.date}：打卡 {selected.count} 次
          {selected.minutes ? ` · 共 ${selected.minutes} 分钟` : ''}
        </p>
      )}
    </div>
  );
}
